import { AlertTriangle, Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ApiError } from "@/lib/api";

/**
 * The one confirmation for everything irreversible: a challenge, a course, a term, an
 * enrollment.
 *
 * Two things make it more than "are you sure?". The description is the server's own
 * 428 sentence -- how many students, submissions, sessions would go -- fetched by the
 * caller and passed in, so the dialog says what *this* deletion costs rather than
 * something generic. And the confirm button stays disabled until the operator has typed
 * the identifier back, which is slow on purpose: the deletions this guards were all
 * built for undoing a mistake, and the way to make a second one is a reflex click.
 */
export function DeleteDialog({
  open,
  onOpenChange,
  title,
  description,
  loadingDescription = false,
  confirmToken,
  confirmLabel = "Delete permanently",
  onConfirm,
  onDone,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** The server's account of what would be destroyed. */
  description: string | null;
  loadingDescription?: boolean;
  /** What must be typed back, exactly. */
  confirmToken: string;
  confirmLabel?: string;
  onConfirm: () => Promise<void>;
  onDone?: () => void;
  /** Anything to show under the description: a list of the rows going, say. */
  children?: React.ReactNode;
}) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);

  async function confirm() {
    setBusy(true);
    try {
      await onConfirm();
      onDone?.();
      onOpenChange(false);
      setTyped("");
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : "Failed", { duration: 12000 });
    } finally {
      setBusy(false);
    }
  }

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        // Closing while the request is in flight would read as "cancelled" when it is not:
        // the delete goes through either way. Hold the dialog until it settles.
        if (!next && busy) return;
        if (!next) setTyped("");
        onOpenChange(next);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="text-destructive flex items-center gap-2">
            <AlertTriangle className="size-4" aria-hidden />
            {title}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {loadingDescription ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="size-3.5 animate-spin" aria-hidden />
                Working out what this would destroy…
              </span>
            ) : (
              description ?? "This cannot be undone."
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {children}

        <div className="grid gap-2">
          <Label htmlFor="confirm-token">
            Type <code className="font-mono">{confirmToken}</code> to confirm
          </Label>
          <Input
            id="confirm-token"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            className="font-mono"
            autoComplete="off"
          />
          <p className="text-muted-foreground text-xs">This cannot be undone.</p>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
          <Button variant="destructive" disabled={busy || loadingDescription || typed !== confirmToken} onClick={confirm}>
            {busy ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
            {confirmLabel}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
